import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useSelector, useDispatch } from "react-redux";
import { createSelector } from "reselect";
import { Card, CardBody, CardHeader, Col, Row, Badge, Table } from "reactstrap";
import { pathologies } from "./patologiasCronicas";
import { createCoverage, editSolicitud } from "../../../slices/thunks";
import { limpiarDatosSolicitud } from "../../../utils/solicitudDataCleaner";

/**
 * InfoCronico es un componente que se encarga de mostrar los detalles de una
 * solicitud de medicamentos de tipo Cronica. Recibe como parámetro un objeto con la
 * información de la solicitud y devuelve un JSX con los detalles de la
 * solicitud.
 *
 * @param {object} props - Objeto con la información de la solicitud.
 *
 * @returns {JSX} Un JSX con los detalles de la solicitud.
 */
const InfoCronico = ({ solicitud }) => {
  const dispatch = useDispatch();

  const nombreCronico =
    pathologies.find((p) => p.codigo === solicitud.cronico.patologia)
      ?.nombre_cronico ?? "Nombre crónico no encontrado";

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString("es-ES", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  };

  const [loadingx, setLoadingx] = useState(false);
  const selectLayoutState = (state) => state;
  const pageData = createSelector(selectLayoutState, (state) => ({
    siscotel: state.Siscotel.siscotel,
    error: state.Siscotel.error,
    loading: state.Siscotel.loading,
    errorMsg: state.Siscotel.errorMsg,
    colorMsg: state.Siscotel.colorMsg,
    exito: state.Siscotel.exito,
  }));

  const { siscotel, error, loading, errorMsg, colorMsg, exito } =
    useSelector(pageData);



  const handleSubmitDiagnostico = async (e) => {
    e.preventDefault();
    setLoadingx(true);
  
    const cobertura = {
      CedulaTitular: solicitud.titularCedula,
      CedulaSolicitante: solicitud.solicitanteCedula,
      CodigoDiagnostico: solicitud.cronico.patologia,
      CodigoProveedor: solicitud.codigoProveedor,
    };
  
    try {
      if (cobertura && cobertura.CodigoDiagnostico) {
        // Enviar la petición a SISCOTEL
        const response = await dispatch(createCoverage(cobertura));
        
        // Verificar si la respuesta fue exitosa
        // En Redux Toolkit, la respuesta exitosa se encuentra en diferentes lugares según la implementación
        const isSuccess = 
          (response && response.payload && response.payload.status === 200) || 
          (response && response.status === 200) ||
          (response && response.type && response.type.endsWith('/fulfilled'));
        
        if (isSuccess) {
          // Obtener los datos de cobertura de la respuesta
          // La respuesta puede estar en diferentes lugares según la implementación de Redux Toolkit
          const siscotelData = response.payload || response;
          
          // Verificar todas las posibles ubicaciones donde podría estar la información de cobertura
          if (
            (siscotelData && siscotelData.coverage) || 
            (siscotelData && siscotelData.payload && siscotelData.payload.coverage) ||
            (siscotelData && siscotelData.data && siscotelData.data.coverage)
          ) {
            // Determinar dónde está el valor de cobertura
            const coverageData = 
              siscotelData.coverage || 
              (siscotelData.payload && siscotelData.payload.coverage) ||
              (siscotelData.data && siscotelData.data.coverage);
            
            //console.log("Valor de cobertura encontrado:", coverageData);
            
            // Procesar el valor de cobertura
            const coverageValue =
              typeof coverageData === "string"
                ? parseFloat(coverageData.replace(" $", ""))
                : typeof coverageData === "number"
                ? coverageData
                : 0;
            
            //console.log("Valor de cobertura procesado:", coverageValue);
  
            // Actualizar la solicitud con los nuevos datos
            const nuevaSolicitud = {
              ...solicitud,
              diagnosticoFarmacologico: nombreCronico,
              codigoDiagnosticoFarmacologico: solicitud.cronico.patologia,
              montoCobertura: coverageValue,
              porcentaje_avance: 20,
            };
  
            // Guardar el ID de la solicitud actual para evitar actualizaciones cruzadas
            const solicitudActualId = solicitud.identificador;
            
            // Actualizar la solicitud específica por su ID
            await dispatch(editSolicitud(limpiarDatosSolicitud(nuevaSolicitud)));
            
            // Almacenar en localStorage el ID de la solicitud que acaba de ser actualizada
            localStorage.setItem('ultimaSolicitudActualizada', solicitudActualId);
            
            // Solo mostrar el mensaje de éxito para esta solicitud específica
            toast.success(
              `Hemos recibido el monto de la cobertura asociada exitosamente para la solicitud #${solicitudActualId}`,
              {}
            );
          } else {
            toast.error("No se pudo obtener el monto de cobertura");
          }
        } else {
          if (solicitud.aseguradora === "MyH+") {
            const errorMsg = response?.siscotelAnswer || response?.payload?.siscotelAnswer || "Error al solicitar monto de cobertura";
            toast.error(`Error en MyH+: ${errorMsg}`);
          }
        }
      }
    } catch (error) {
      if (solicitud.aseguradora === "MyH+") {
        const errorMsg = error?.message || "Error al procesar la solicitud de cobertura";
        toast.error(`Error en MyH+: ${errorMsg}`);
      }
    } finally {
      setLoadingx(false);
    }
  };

  return (
    <React.Fragment>
      <Col>
        <Card>
          <CardHeader className="bg-soft-primary">
            <div className="d-flex align-items-center">
              <div className="flex-shrink-0">
                <i className="ri-medicine-bottle-line text-primary fs-22"></i>
              </div>
              <div className="flex-grow-1 ms-3">
                <h5 className="card-title mb-0 fw-semibold">
                  Información Crónica
                </h5>
              </div>
            </div>
          </CardHeader>
          <CardBody>
            <div className="table-responsive table-card">
              <Table className="table-borderless align-middle mb-0">
                <tbody>
                  <tr>
                    <td style={{ width: "35%" }}>
                      <div className="d-flex align-items-center">
                        <i className="ri-virus-line text-primary me-2 fs-16"></i>
                        <span className="fw-medium">Patología</span>
                      </div>
                    </td>
                    <td>
                      <span className="badge bg-primary-subtle text-primary fs-12 me-2">
                        {solicitud.cronico.patologia}
                      </span>
                      {nombreCronico}
                    </td>
                  </tr>
                  <tr>
                    <td>
                      <div className="d-flex align-items-center">
                        <i className="ri-calendar-check-line text-primary me-2 fs-16"></i>
                        <span className="fw-medium">Fecha Creación</span>
                      </div>
                    </td>
                    <td>{formatDate(solicitud.cronico.fechaCreacion)}</td>
                  </tr>
                  <tr>
                    <td>
                      <div className="d-flex align-items-center">
                        <i className="ri-calendar-event-line text-primary me-2 fs-16"></i>
                        <span className="fw-medium">Fecha Vigencia</span>
                      </div>
                    </td>
                    <td>{formatDate(solicitud.cronico.fechaVigencia)}</td>
                  </tr>
                  <tr>
                    <td>
                      <div className="d-flex align-items-center">
                        <i className="ri-medicine-bottle-line text-primary me-2 fs-16"></i>
                        <span className="fw-medium">
                          Medicamentos Solicitados
                        </span>
                      </div>
                    </td>
                    <td>{solicitud.cronico.medicamentosSolicitados}</td>
                  </tr>
                  <tr>
                    <td>
                      <div className="d-flex align-items-center">
                        <i className="ri-capsule-line text-primary me-2 fs-16"></i>
                        <span className="fw-medium">
                          Medicamentos Bioequivalentes
                        </span>
                      </div>
                    </td>
                    <td>{solicitud.cronico.medicamentosBioequivalentes}</td>
                  </tr>
                </tbody>
              </Table>
            </div>

            {/* Botón de Consultar Cobertura eliminado - ahora se consulta automáticamente al registrar medicamentos */}
            <div className="alert alert-info mt-3 mb-0">
              <i className="ri-information-line me-2"></i>
              <strong>Nota:</strong> La cobertura se consulta automáticamente al registrar medicamentos en la sección "Registro de Medicamentos Crónicos".
            </div>
          </CardBody>
        </Card>
      </Col>
    </React.Fragment>
  );
};

// Optimización: React.memo para evitar re-renders innecesarios
export default React.memo(InfoCronico, (prevProps, nextProps) => {
  return prevProps.solicitud?.id === nextProps.solicitud?.id &&
         prevProps.solicitud?.actualizado === nextProps.solicitud?.actualizado;
});
